/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {BaseComponent, ToastModule} from "@intuitionrobotics/thunderstorm/frontend";
import {COLORS} from "@res/colors";
import {StatusDialog} from "./StatusDialog";
import {hideTooltip, showTooltip} from "../../widget/appTooltip";
import {margin_rl_med, padding_rl_med, Spacing} from "@styles/styles";
import {FONTS} from "@res/fonts";
import * as emotion from "emotion";
import {IconArrows} from "@res/icons-tsx/IconArrows";
import {TS_Table} from "../../../_TS_TABLE";
import {MessageFilter, messageTableHeadHeight, messagingTableRowHeight} from "./Page_Messages";
import {IconInfo} from "../../widget/IconInfo";
import {OnWindowResize} from "../../../App";
import {CCInbox} from "@app/ir-q-app-common/types/message-api";
import {DateAndTimeCell} from "./InboxTableCells/DateAndTimeCell";
import {PatientCell} from "./InboxTableCells/PatientCell";
import {StatusCell} from "./InboxTableCells/StatusCell";
import {UserCell} from "./InboxTableCells/UserCell";
import {MarkAsReadIcon} from "./InboxTableCells/MarkAsReadIcon";
import {LastMessage} from "./InboxTableCells/LastMessage";
import {InfoCell} from "./InboxTableCells/InfoCell";
import {MessagingModule} from "@modules/MessagingModule";
import {Loader} from "../../../../widgets/Loader";
import {sortArray} from "@intuitionrobotics/ts-common";
import {TimeAndPretty} from "./CalendarDialog";
import {capitalize} from "@app/ir-q-app-common/types/care-center/user";
import {calculatePagination, PaginationComponent} from "../../widget/PaginationComponent";
import {DB_Chat, DB_Message} from "@app/ir-q-app-common/types/message";
import {DB_Patient} from "@app/app-shared/patient";
import {PatientsContext, PatientsContextType, PatientsMap} from "../patients/PatientsContext";
import {ChatsContext, ChatsContextType} from "./ChatsContext";
import "simple-ts-date-picker/dist/simple_datepicker.css";
import {ContactsContext, ContactsMap} from "./ContactsContext";

export const backgroundCell = (backgroundColor: string) => emotion.css({backgroundColor});

type State = {
    page: number,
    chatBoxOpenedRow?: number
    sort: { column: keyof TableChatRow, ascending: boolean },
};

type Props = {
    search: string
    filterDate?: {
        start: TimeAndPretty
        end: TimeAndPretty
    },
    chatId?: string
    onSelected: (recipient: string, chatId: string) => void
    selectedPatientContactId?: string,
    selectedGroupContactId?: string
    filterByMessage: MessageFilter
    orgFilter: Set<string>
    demo: boolean
};


export type TableChatRow = {
    info: CCInbox
    to: CCInbox
    message: CCInbox
    status: CCInbox
    from: CCInbox,
    // group: CCInbox,
    dateAndTime: CCInbox
    markAsRead: CCInbox
}

export class InboxTable
    extends BaseComponent<Props, State>
    implements OnWindowResize {

    constructor(props: Props) {
        super(props);
        this.state = {
            page: 1,
            sort: {column: "dateAndTime", ascending: false}
        };
    }

    private tableRef = React.createRef<any>();

    __onWindowResize = () => {
        this.forceUpdate();
    };

    private getChatsContent = (contacts: ContactsMap, chats: CCInbox[], {patients: pMap}: PatientsContextType) => {
        const includedChats: string[] = [];

        const contactToPatient = Object.values(pMap).reduce((carry: PatientsMap, patient) => patient ? ({...carry, [patient.contactId]: patient}) : {}, {})
        return chats.reduce((carry: TableChatRow[], chat: CCInbox, index: number) => {
            const chatContacts = chat.contactIds.map(cId => contacts[cId]);

            const agentUserContact = chatContacts.find(contact => contact?.contactType === "agentUser");
            if (!agentUserContact)
                return carry;

            const patient = contactToPatient[agentUserContact._id];
            if (!patient)
                return carry;

            if (this.props.orgFilter.size && !this.props.orgFilter.has(patient.organizationId))
                return carry;

            const messageFilter = this.props.filterByMessage;

            if (messageFilter === MessageFilter.ALL
                || (chat.unreadCount > 0 && messageFilter === MessageFilter.UNREAD)
                || (agentUserContact._id !== chat.lastMessage.senderId && messageFilter === MessageFilter.NO_REPLY)) {

                if (!includedChats.includes(chat._id)) {
                    carry.push({
                        info: chat,
                        to: chat,
                        message: chat,
                        status: chat,
                        dateAndTime: chat,
                        from: chat,
                        // group: chat,
                        markAsRead: chat
                    });
                    // Need to check this logic to include also the sender and the reply in case there is one!
                    includedChats.push(chat._id);
                }
            }


            return carry;
        }, []);
    };


    sortChatsByDate = (filtered: TableChatRow[]) => {
        return sortArray(filtered, row => row.dateAndTime.lastMessage.timestamp, this.state.sort.ascending);
    };

    sortChatsByStatus = (filtered: TableChatRow[]) => {
        return sortArray(filtered, (row) => this.chatStatusCallback(row.status), this.state.sort.ascending);
    };

    private chatStatusCallback = (chat: CCInbox) => {
        if (chat.lastMessage.replyId)
            return "Answered";

        return capitalize(chat.lastMessage.status.status);
    };

    sortChatsBySender = (filtered: TableChatRow[]) => {
        return sortArray(filtered, row => row.from.lastMessage.senderId, this.state.sort.ascending);
    };

    sortChatsByReceiver = (filtered: TableChatRow[]) => {
        return sortArray(filtered, row => row.to.contactIds.find((contactId: string) => contactId !== row.to.lastMessage.senderId), this.state.sort.ascending);
    };

    private getContentData(contacts: ContactsMap, cCtx: ChatsContextType, pctx: PatientsContextType) {
        const chats = cCtx.chats;

        if (!chats)
            return;

        return this.getChatsContent(contacts, Object.values(chats), pctx);
    }

    private getFilteredContent = (chats: TableChatRow[], {patients: pMap}: PatientsContextType) => {

        const filterDate = this.props.filterDate;

        if (!this.props.search && !filterDate)
            return chats;

        const search = this.props.search?.toLowerCase() || "";

        const recipientsMap = Object.values(pMap).reduce((carry: PatientsMap, patient) => patient ? ({...carry, [patient.contactId]: patient}) : {}, {})

        // Need to handle filter better
        const _filter = (chat: TableChatRow) => {

            let filter = search === "";

            if (!filter) {
                if (chat.to.lastMessage.type === "text" && chat.to.lastMessage.data.text.toLowerCase().includes(search))
                    filter = true;
            }

            if (!filter) {
                const patient = chat.to.contactIds.map(contactId => recipientsMap[contactId]).find(Boolean);
                if (patient?.name.toLowerCase().includes(search) || patient?.unitId.includes(search))
                    filter = true;
            }

            if (filterDate)
                filter = filter && chat.from.lastMessage.timestamp >= filterDate.start.timestamp && chat.from.lastMessage.timestamp <= filterDate.end.timestamp;

            return filter;
        };
        return chats.filter(_filter);
    };

    setSort = (column: keyof TableChatRow) => {
        this.setState(state => {
            if (column === "dateAndTime") {
                if (state.sort.column === column)
                    return {
                        ...state,
                        sort: {column: column, ascending: !state.sort.ascending}
                    };

                return {
                    ...state,
                    sort: {column: "dateAndTime", ascending: false}
                };
            }

            // Ascending order for new column.
            if (state.sort.column !== column)
                return {
                    ...state,
                    sort: {column: column, ascending: true}
                };

            if (!state.sort.ascending)
                //	Return to default, which is descending date.
                return {
                    ...state,
                    sort: {column: "dateAndTime", ascending: false}
                };

            return {
                ...state,
                sort: {column: column, ascending: false}
            };
        });
    };

    private headerStyle = (title?: string, options?: { sortable?: boolean }, column?: keyof TableChatRow) => {
        return <div
            className={`ll_h_c ${margin_rl_med} ${options && options.sortable ? "clickable" : "cursor_default"}`}
            onClick={() => {
                if (!column)
                    return;

                this.setSort(column);
            }}>
            {title && FONTS.reg_14_secondary_dark(title, {whiteSpace: "nowrap", userSelect: "none"})}
            {options && options.sortable &&
                <IconArrows
                    style={{marginLeft: Spacing.l}}
                    colorOn={COLORS.neutral_text_dark}
                    up={this.state.sort.column === column && this.state.sort.ascending}
                    down={this.state.sort.column === column && !this.state.sort.ascending}
                />
            }
        </div>;
    };

    static rowStyle = (content: string, bold?: boolean, failed?: boolean, style?: React.CSSProperties) => {
        const color = failed ? COLORS.secondary_red : COLORS.neutral_text_dark;
        const textStyle = {color, lineHeight: "24px"}; // line height should be at least half of row height for maximum of 2 lines
        const blockWithText = emotion.css({overflow: "hidden", maxHeight: `${messagingTableRowHeight}px`});

        return <div
            className={`ll_h_c ${padding_rl_med}`}
            style={{height: 47, cursor: "pointer", ...style}}
        >
            <div className={blockWithText}>
                {bold ? FONTS.semi_bold_16(content, textStyle) : FONTS.reg_16(content, textStyle)}
            </div>
        </div>;
    };

    private header: (keyof TableChatRow)[] = ["info",
        "dateAndTime",
        "to",
        "message",
        "from",
        "markAsRead"];

    private setChat = (contacts: ContactsMap, chat: CCInbox, rowIndex: number) => {
        const chatId = chat._id;
        const {groupContactId, userContactId} = this.getRelevantContactIds(contacts, chat);

        if (!userContactId || !groupContactId)
            return ToastModule.toastError("Something went wrong, please reload the page and try again...");

        this.props.onSelected(userContactId, chatId);

        this.setState({chatBoxOpenedRow: rowIndex});

        if (MessagingModule.getNumberOfUnreadMessages(chat) > 0 && !this.props.demo) {
            MessagingModule.setAsRead(chat.lastMessage._id, groupContactId).then(() => {
                chat.unreadCount = 0;
                this.forceUpdate()
            }).catch(console.error);
        }
    };

    private getRelevantContactIds(contacts: ContactsMap, chat: DB_Chat) {
        const relevantContacts = chat.contactIds.map(cId => contacts[cId]);

        const groupContactId = relevantContacts.find(contact => contact.contactType === "group")?._id;
        const userContactId = relevantContacts.find(contact => contact.contactType === "agentUser")?._id;
        return {groupContactId, userContactId};
    }

    sort = (table: TableChatRow[]) => {
        switch (this.state.sort.column) {
            case "dateAndTime":
                return this.sortChatsByDate(table);
            case "from":
                return this.sortChatsBySender(table);
            case "status":
                return this.sortChatsByStatus(table);
            case "to":
                return this.sortChatsByReceiver(table);
            default:
                return table;
        }
    };

    render() {
        return <ChatsContext.Consumer>
            {cCtx => <ContactsContext.Consumer>
                {contactsCtx =>
                    <PatientsContext.Consumer>
                        {
                            pctx => {
                                if (!pctx.patientIds.length)
                                    return <></>

                                const contentData = this.getContentData(contactsCtx.contacts, cCtx, pctx); //here get new org.
                                if (!contentData)
                                    return <Loader/>;
                                const filteredChats: TableChatRow[] = this.getFilteredContent(contentData || [], pctx);
                                const content = this.sort(filteredChats);
                                return this.renderTable(contactsCtx.contacts, content, pctx);
                            }
                        }
                    </PatientsContext.Consumer>}

            </ContactsContext.Consumer>}
        </ChatsContext.Consumer>
    }

    private renderTable(contacts: ContactsMap, sortedChats: TableChatRow[], {patients: pMap}: PatientsContextType) {
        const pagination = calculatePagination(this.state.page, this.tableRef.current);
        const slicedTableContent = !!pagination ? sortedChats.slice(pagination.startIndex, pagination.endIndex) : [];
        return <div
            className={"ll_v_l space-between match_height"}
            ref={this.tableRef}
        >
            <TS_Table<TableChatRow>
                style={{maxHeight: "100%", overflowY: "hidden"}}
                id={"all-patients-message-table"}
                tr={() => ({
                    style: {
                        borderBottom: `1px solid #f1f5f8`,
                        cursor: "pointer",
                        height: "100%"
                    },
                    className: emotion.css(
                        {
                            "&:hover > * > *": {backgroundColor: "#f1f5f8"}
                        }
                    )
                })}
                trHead={{style: {boxShadow: "0 0 6px 3px #f4f4f4"}}}
                td={{
                    className: "match-height",
                    style: {borderBottom: `1px solid ${COLORS.color_gray_medium}`, cursor: "pointer"}
                }}
                th={(key) => {
                    const className = emotion.css(
                        {
                            position: "sticky",
                            top: 0,
                            zIndex: 10,
                            height: messageTableHeadHeight,
                            backgroundColor: COLORS.white,
                            minWidth: "100px"
                        });
                    let style: React.CSSProperties = {};
                    switch (key) {
                        case "message":
                            style = {width: "100%", minWidth: "200px"};
                            break;
                        case "to":
                            style = {minWidth: "220px"};
                            break;
                        case "info":
                            style = {minWidth: "5px"};
                            break;
                        case "from":
                            style = {minWidth: "180px"};
                            break;
                        case "status":
                            style = {minWidth: "150px"};
                            break;
                        case "dateAndTime":
                            style = {minWidth: "200px"};
                            break;
                        default:
                            break;
                    }
                    return {className, style};
                }}
                header={this.header}
                rows={slicedTableContent}
                headerRenderer={{
                    info: () => this.headerStyle("", {}, "info"),
                    dateAndTime: () => this.headerStyle("Date", {sortable: true}, "dateAndTime"),
                    to: () => this.headerStyle("Customer", {sortable: true}, "to"),
                    message: () => this.headerStyle("Message", {}, "message"),
                    status: () => <div
                        style={{position: "relative"}}>{this.headerStyle("Status", {sortable: true}, "status")}
                        <div style={{position: "absolute", top: 0, left: 65}}>
                            <IconInfo
                                onMouseOver={(e) => {
                                    const rect = e.currentTarget.getBoundingClientRect();
                                    showTooltip(e, <StatusDialog/>,
                                        rect.left,
                                        rect.bottom + 10,
                                        {
                                            textAlign: "left",
                                            backgroundColor: "white",
                                            borderRadius: "4px",
                                            transform: "translate(-100%)",
                                            border: `1px solid ${COLORS.primary_light_blue}`
                                        });
                                }}
                                onMouseLeave={() => {
                                    hideTooltip();
                                }}
                            />
                        </div>
                    </div>,
                    from: () => this.headerStyle("User", {sortable: true}, "from"),
                    markAsRead: () => this.headerStyle()
                }}
                cellRenderer={{
                    info: (chat: CCInbox) => <InfoCell chat={chat}
                                                       isOpen={this.checkIfChatOpen(slicedTableContent, chat)}/>,
                    dateAndTime: (chat: CCInbox, rowIndex: number) => <DateAndTimeCell chat={chat}
                                                                                       onClick={() => this.setChat(contacts, chat, rowIndex)}
                                                                                       isOpen={this.checkIfChatOpen(slicedTableContent, chat)}
                                                                                       getUnreadMessages={() => MessagingModule.getNumberOfUnreadMessages(chat)}/>,
                    to: (chat: CCInbox, rowIndex: number) => <PatientCell
                        chat={chat}
                        onClick={() => this.setChat(contacts, chat, rowIndex)}
                        showUnitId={true}
                        isOpen={this.checkIfChatOpen(slicedTableContent, chat)}
                        recipient={() => {
                            const isResponse = MessagingModule.isResponse(contacts, chat);
                            let patientContactId: string;
                            if (isResponse) {
                                const lastMessage = chat.lastMessage as DB_Message<any>;
                                patientContactId = lastMessage.senderId;
                            } else {
                                const chatContacts = chat.contactIds.map(cId => contacts[cId]);
                                patientContactId = chatContacts.find(
                                    _contact => _contact.contactType === "agentUser")?._id || "";
                            }

                            return Object.values(pMap).find(p => p?.contactId === patientContactId) as DB_Patient;
                        }}
                        getUnreadMessages={() => MessagingModule.getNumberOfUnreadMessages(chat)}
                    />,
                    message: (chat: CCInbox, rowIndex: number) => <LastMessage chat={chat}
                                                                               onClick={() => this.setChat(contacts, chat, rowIndex)}
                                                                               isOpen={this.checkIfChatOpen(slicedTableContent, chat)}
                                                                               getUnreadMessages={() => MessagingModule.getNumberOfUnreadMessages(chat)}
                    />,
                    status: (chat: CCInbox, rowIndex: number) => <StatusCell chat={chat}
                                                                             onClick={() => this.setChat(contacts, chat, rowIndex)}
                                                                             isOpen={this.checkIfChatOpen(slicedTableContent, chat)}
                                                                             getUnreadMessages={() => MessagingModule.getNumberOfUnreadMessages(chat)}
                    />,
                    from: (chat: CCInbox, rowIndex: number) => <UserCell chat={chat}
                                                                         onClick={() => this.setChat(contacts, chat, rowIndex)}
                                                                         isOpen={this.checkIfChatOpen(slicedTableContent, chat)}/>,
                    markAsRead: (chat: CCInbox) => <MarkAsReadIcon chat={chat}
                                                                   isOpen={this.checkIfChatOpen(slicedTableContent, chat)}
                                                                   getUnreadMessages={() => MessagingModule.getNumberOfUnreadMessages(chat)}
                                                                   markAsRead={() => MessagingModule.setAsRead(chat.lastMessage._id, this.getRelevantContactIds(contacts, chat).groupContactId as string).then(() => {
                                                                       chat.unreadCount = 0;
                                                                       this.forceUpdate()
                                                                   })}
                                                                   markAsUnread={() => MessagingModule.setAsUnread(chat, this.getRelevantContactIds(contacts, chat).userContactId as string).then(() => {
                                                                       chat.unreadCount = chat.unreadCount + 1;
                                                                       this.forceUpdate()
                                                                   })}
                    />
                }}
            />

            <PaginationComponent
                pagination={pagination}
                tableContent={sortedChats}
                setCurrentPage={(page: number) => this.setState({page})}
            />
        </div>
    }

    private checkIfChatOpen(slicedTableContent: TableChatRow[], chat: DB_Chat) {
        if (this.state.chatBoxOpenedRow && this.state.chatBoxOpenedRow === slicedTableContent.findIndex(row => row.message._id === chat._id))
            return true;

        return !!this.props.chatId && chat._id === this.props.chatId;


    }
}
